import { Input } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
import type React from 'react';
import { Input as InputStrapTyped } from 'reactstrap';
import useSuggestInput from './useSuggestInput';
import './styles.scss';

const { TextArea } = Input;

// reactstrap's <Input> types don't include `showCount` (an antd-only concept);
// this component forwards it through regardless — pre-existing behaviour.
const InputStrap = InputStrapTyped as any;

export type SuggestInputProps = {
  className?: string;
  name: string;
  type?: string;
  placeholder?: string;
  label?: string;
  value?: string | number;
  disabled?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onChange?: (name: string, value: string, index?: number) => void;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  classNameLabel?: string;
  linkText?: string;
  index?: number;
  suffix?: string;
  maxLength?: number;
  suggestTexts?: string[];
  isFlexibleHeight?: boolean;
  showCount?: boolean;
  // used in JSX below but never declared in the original PropTypes
  isReverse?: boolean;
  ariaLabel?: string;
};

const SuggestInput = ({
  className,
  name,
  type = 'text',
  placeholder = '',
  label = '',
  value = '',
  disabled = false,
  isError = false,
  errorMessage = '',
  onChange = () => {},
  classNameLabel = '',
  id,
  onKeyDown = () => {},
  linkText = '',
  index,
  suffix = null as any,
  maxLength,
  suggestTexts = [],
  isFlexibleHeight = false,
  showCount = true,
  isReverse,
  ariaLabel,
}: SuggestInputProps) => {
  const { inputElem, handleChange, insertText } = useSuggestInput({
    name,
    onChange,
    index,
    id,
    maxLength,
    onKeyDown,
  });
  // No htmlFor/label association here (would make the label text
  // click-to-focus the input, a UI behaviour change) — the visible label
  // doubles as the accessible name via aria-label instead.
  const resolvedAriaLabel = ariaLabel || label || undefined;

  return (
    <div
      id={`text-input-wrapper-${id}`}
      className={classnames(
        'custom-text-input suggest-input-wrapper',
        className,
        suffix ? '--suffix' : '',
      )}
    >
      {!!label && (
        <div className={classnames('custom-text-input__label', classNameLabel)}>{label}</div>
      )}
      <div
        className={classnames('custom-suggest-text-input-wrapper', isReverse ? '--is-reverse' : '')}
      >
        {!_.isEmpty(suggestTexts) && !disabled && (
          <div className="suggest-texts-wrapper">
            {_.map(suggestTexts, (text, index) => (
              <button
                id={`suggest-text-button-${index}`}
                key={`${text}-${index}`}
                disabled={disabled}
                className="suggest-text-button"
                onClick={() => {
                  insertText(text);
                }}
              >
                {text}
              </button>
            ))}
          </div>
        )}
        <div className="custom-text-input__input-wrapper">
          {isFlexibleHeight ? (
            <TextArea
              className={classnames(
                'custom-text-input__input --flexible-height',
                type === 'textarea' ? '--textarea' : '',
                isError ? '--error' : '',
              )}
              id={id}
              aria-label={resolvedAriaLabel}
              name={name}
              disabled={disabled}
              placeholder={placeholder}
              value={value}
              onChange={handleChange}
              onKeyDown={onKeyDown}
              readOnly={false}
              maxLength={maxLength}
              ref={inputElem}
              autoSize={{ minRows: 3, maxRows: 10 }}
              showCount={showCount}
            />
          ) : (
            <div
              className={classnames(
                'strap-custom-text-input',
                showCount && maxLength ? '--show-count' : '',
                isError ? '--error' : '',
                disabled ? '--disabled' : '',
              )}
            >
              <InputStrap
                className={classnames(
                  'custom-text-input__input',
                  type === 'textarea' ? '--textarea' : '',
                  isError ? '--error' : '',
                )}
                id={id}
                aria-label={resolvedAriaLabel}
                name={name}
                type={type as any}
                disabled={disabled}
                placeholder={placeholder}
                value={value || ''}
                onChange={handleChange}
                onKeyDown={onKeyDown}
                readOnly={false}
                maxLength={maxLength}
                ref={inputElem}
                showCount
              />
              {showCount && maxLength && !disabled && (
                <small className="show-count-text">
                  {`${String(value)?.length || 0} / ${maxLength}`}
                </small>
              )}
            </div>
          )}
          {isError && !!errorMessage && (
            <div className={linkText ? 'flex justify-between items-center' : undefined}>
              <div className="custom-text-input__error-message">{errorMessage}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuggestInput;
